import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { firstValueFrom, forkJoin, from, of } from "rxjs";
import { LoadingService } from "../../loading/loading.service";
import { AcuerdosService } from "../../services/acuerdos/acuerdos.service";
import {
  cancelarFormulariosAcuerdo,
  cargarDatosGuardados,
  guardarFormularios,
  guardarFormulariosAcuerdo,
  guardarFormulariosFailure,
  guardarFormulariosSuccess,
  guardarNuevoTema,
  guardarNuevoTemaFailure,
  guardarNuevoTemaSuccess,
  resetForms,
} from "./actions";
import { AcuerdoRequest, ICNuevoTema } from "../../interfaces/acuerdo";

import {
  selectAcuerdoSelected,
  selectAcuerdoSelectedAcuerdo,
  selectAcuerdoTemas,
  selectArchivosemas,
} from "../temas/selectors";
import { AppState, appStore } from "../../app.state";
import { selectValidarForms } from "./selector";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmDialogComponent } from "../../confirm-dialog/confirm-dialog.component";
import {
  cargaTemasAcuerdo,
  resetTemasAcuerdo,
  resetTemasDejarAcuerdo,
} from "../temas/actions";
import {
  cargaAcuerdosFail,
  cargarAcuerdosEditados,
  expandAcuerdo,
  resetAcuerdosByTipo,
} from "../acuerdos-mrb/actions";
import {
  dtoDetalleTema,
  dtoNuevoTema,
  validateCamposNuevoTema,
  validateCamposObligatorios,
} from "../../formas-envio/acuerdo/detalle-tema/dtoDetalleTema";
import { selectTipoAcuerdo } from "../acuerdos-mrb/selector";
import { IAcuerdo, IAcuerdos } from "../../interfaces/acuerdos";
import { StorageService } from "../../services/storage/files.service";
import { DespachoService } from "../../services/despacho/despacho.service";
import { setContadorSuccess } from "../despacho/actions";
appStore;
@Injectable()
export class AcuerdoEffects {
  constructor(
    private actions$: Actions,
    private acuerdoService: AcuerdosService,
    private store: Store<AppState>,
    private loadingService: LoadingService,
    public dialog: MatDialog,
    public storageService: StorageService,
    public despachoService: DespachoService
  ) {}

  guardarFormulariosAcuerdo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(guardarFormulariosAcuerdo),
      withLatestFrom(
        this.store.select(selectAcuerdoSelectedAcuerdo),
        this.store.select(selectArchivosemas)
      ),
      mergeMap(([action, acuerdo, archivos]) => {
        this.loadingService.show();
        this.dialog.closeAll();
        const temas = action.temas;
        const acuerdoActual = {
          ...acuerdo,
          cambioObservacion:
            acuerdo.observaciones != action.acuerdo.observaciones &&
            action.acuerdo.observaciones?.trim() != "",
          observaciones: action.acuerdo.observaciones,
          esRealizado: action.acuerdo.esRealizado,
        };
        const dtoDetalle = dtoDetalleTema(acuerdoActual, temas);
        const dtoDetalleNuevosTemas = dtoNuevoTema(temas);
        const camposValidos = validateCamposObligatorios(dtoDetalle.temas);
        const camposValidosNuevoTema = validateCamposNuevoTema(
          dtoDetalleNuevosTemas
        );
        if (camposValidos && camposValidosNuevoTema) {
          const acuerdoRequest: AcuerdoRequest = {
            ...dtoDetalle,
          };

          return this.acuerdoService.editarAcuerdo(acuerdoRequest).pipe(
            map((response) => {
              if (!response.error) {
                const docs = {
                  idAcuerdo: acuerdo.idFE,
                  archivos: action.archivos??[],
                };
                if (docs.archivos.length > 0) {
                  const soloNuevos = docs.archivos.filter((file)=>file.nuevo);
                  this.storageService
                    .guardarArchivos(docs.idAcuerdo, soloNuevos)
                    .subscribe((dta) => {});
                }
               
                return guardarNuevoTema({
                  temas: dtoDetalleNuevosTemas,
                  acuerdoActual: acuerdoActual,
                  request: acuerdoRequest,
                });
              } else if (response.error) {
                this.loadingService.hide();
                this.showDialog("Alerta", response.message.msg);

                return guardarFormulariosFailure({ error: "error" });
              } else {
                return guardarFormulariosFailure({ error: "error" });
              }
            }),
            catchError((error) => {
              this.loadingService.hide();
              return of(guardarFormulariosFailure({ error }));
            })
          );
        } else {
          this.loadingService.hide();
          this.showDialog(
            "Atención",
            "Favor de capturar todos los datos obligatorios en cada tema."
          );
          return of(
            guardarFormulariosFailure({
              error: { error: true, message: "Campos inválidos" },
            })
          );
        }
      }),
      catchError((error) => of(guardarFormulariosFailure({ error })))
    )
  );

  cancelarFormulariosAcuerdo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cancelarFormulariosAcuerdo),
      withLatestFrom(
        this.store.select(selectAcuerdoSelectedAcuerdo),
        this.store.select(selectArchivosemas)
      ),
      map(([action, acuerdo, archivos]) => {
        this.loadingService.show();
        const temas = action.temas.filter((tema: any) => !tema.nuevoTema);

        return cargarDatosGuardados({ temas });
      }),
      catchError((error) => {
        return of(guardarFormulariosFailure({ error }));
      })
    )
  );

  guardarNuevoTema$ = createEffect(() =>
    this.actions$.pipe(
      ofType(guardarNuevoTema),
      switchMap(({ temas, acuerdoActual, request }) => {
        this.loadingService.show();

        if (temas.length === 0) {
          this.store.dispatch(cargarDatosGuardados({ temas: request.temas }));
          return of(
            guardarNuevoTemaSuccess({ response: "No new temas to save" })
          );
        }

        const camposValidos = validateCamposNuevoTema(temas);

        if (camposValidos) {
          return from(temas).pipe(
            concatMap((nuevoTema) =>
              this.acuerdoService.nuevoTema(nuevoTema).pipe(
                map((response) => {
                  return response;
                }),
                catchError((error) => {
                  return of(error);
                })
              )
            ),
            finalize(() => {
              this.store.dispatch(
                cargarDatosGuardados({ temas: request.temas })
              );
              this.store.dispatch(resetForms());
            }),
            map((responses) => {
              this.store.dispatch(
                expandAcuerdo({ idAcuerdo: acuerdoActual.idFE })
              );
              return guardarNuevoTemaSuccess({ response: responses });
            }),
            catchError((error) => {
              this.loadingService.hide();
              this.showDialog(
                "Atención",
                "Ocurrió un error al guardar los temas."
              );
              return of(guardarNuevoTemaFailure({ error }));
            })
          );
        } else {
          this.loadingService.hide();
          this.showDialog(
            "Atención",
            "Favor de capturar todos los datos obligatorios en cada tema."
          );
          return of(
            guardarNuevoTemaFailure({
              error: { error: true, message: "Campos inválidos" },
            })
          );
        }
      }),
      catchError((error) => {
        this.loadingService.hide();
        return of(guardarNuevoTemaFailure({ error }));
      })
    )
  );

  cargarDatosGuardados$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cargarDatosGuardados),
      withLatestFrom(
        this.store.select(selectTipoAcuerdo),
        this.store.select(selectAcuerdoSelectedAcuerdo),
        this.store.select(selectAcuerdoTemas)
      ),
      mergeMap(([data, tipoAcuerdo, acuerdoSeleccionado, temas]) => {
        this.loadingService.show();
        console.log("cargarDatosGuardados", data);

        return this.acuerdoService.getAllAcuerdosByTipo(tipoAcuerdo).pipe(
          map((response: IAcuerdos) => {
            console.log("response", response);

            let acuerdos: IAcuerdo[] = [];
            let _acuerdo: IAcuerdo | undefined;
            const acuerdoMap: any = {
              1: response.acuerdoActual,
              2: response.acuerdos,
              3: response.historico,
            };

            console.log("acuerdos", acuerdos);
            console.log("_acuerdo", _acuerdo);
            acuerdos = acuerdoMap[tipoAcuerdo] || [];
            // if (tipoAcuerdo == 1 || tipoAcuerdo == 2) {
            //  this.despachoService
            //     .getInfoDespacho().pipe(take(1)).subscribe((infoDespacho)=>{
            //       console.log("infoDespacho", infoDespacho);

            //       const contador = {
            //         acuerdo: acuerdos[0].temas.toString(),
            //         despacho: `${infoDespacho.vobo}-${infoDespacho.informativo}`,
            //       };
            //         this.store.dispatch(setContadorSuccess({contador}))
            //     })
            // }

            acuerdos = acuerdos.map((acuerdo) => {
              if (acuerdo.idFE === acuerdoSeleccionado.idFE) {
                return {
                  ...acuerdo,
                  expanded: true,
                };
              }
              return acuerdo;
            });

            console.log("acuerdos", acuerdos);
            _acuerdo = acuerdos.find(
              (acuerdo) => acuerdo.idFE === acuerdoSeleccionado.idFE
            );

            console.log("_acuerdo", _acuerdo);
            if (_acuerdo) {
              _acuerdo = {
                ..._acuerdo,
                expanded: true,
              };
            }
            const temaExpandir = data.temas.find(
              (tema: any) => tema.verTema || tema.expanded
            );

            console.log("temaExpandir", temaExpandir);
            const updatedAcuerdo = _acuerdo || acuerdoSeleccionado;

            console.log("updatedAcuerdo", updatedAcuerdo);
            console.log("id", temaExpandir);
            console.log(
              "temaExpandir",
              temaExpandir && temaExpandir.indiceTema
            );

          const id =
            temaExpandir && temaExpandir.indiceTema !== undefined
              ? temaExpandir.indiceTema
              : null;

            this.store.dispatch(
              cargaTemasAcuerdo({
                acuerdoSeleccionado: {
                  acuerdo: updatedAcuerdo,
                  temas: [],
                  archivos: [],
                  status: "editTemaAcuerdo",
                  ultimoTemaGuardado: id,
                },
              })
            );
            return cargarAcuerdosEditados({ acuerdos, tipoAcuerdo });
          }),
          catchError((error) => {
            console.log("cargaAcuerdosFail======");

            this.loadingService.hide();
            return of(cargaAcuerdosFail());
          })
        );
      })
    )
  );
  private showDialog(title: string, message: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: "450px",
      data: {
        title,
        message,
        showButtons: {
          accept: true,
          cancel: false,
        },
        direction: "end",
        buttonsName: { cancel: "CANCELAR", accept: "ACEPTAR" },
      },
    });
  }
}
